import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { db } from '../firebase/firebaseConfig';
import { collection, doc, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import {
  FaExclamationCircle,
  FaExclamationTriangle,
  FaShieldAlt,
  FaLock,
  FaFileMedical,
  FaTools,
  FaComments,
  FaCalendarAlt,
  FaClipboardCheck,
  FaChartBar,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaTimes,
  FaFilter,
  FaTrash
} from 'react-icons/fa';
// Import decryption utilities for PHI/PII data
import { decryptDocuments } from '../utils/encryption';
// Import date formatting utility for timezone-aware display
import { formatDateTime, formatDate, isDateField, isDateValue, isDateOnlyValue } from '../utils/dateFormatter';
// Import organization utilities for multi-tenancy filtering
import { matchesOrganization } from '../utils/organization';
import { toast } from 'react-toastify';
import { toUserMessage } from '../utils/userError';

const DataView = ({ practice: userPractice = '', organization = 'development', userRole = '' }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('');
  // userPractice comes from props (activePractice passed from App.js)
  // userRole is also passed as a prop from App.js to avoid race conditions
  const [buttonCounts, setButtonCounts] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedFilterFields, setSelectedFilterFields] = useState(new Set());
  const [pendingFilters, setPendingFilters] = useState({});
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const dataByTypeRef = useRef({});
  const dataViewerRef = useRef(null);
  const activeListenersCleanupRef = useRef(null); // Store cleanup function for active listeners
  const refreshAfterDeleteRef = useRef(null); // Callback to re-fetch data after delete
  const lastFetchParamsRef = useRef({ collection: null, subCollection: null, label: null });
  
  // Helper function to decrypt and set data
  // This ensures all encrypted PHI/PII fields are decrypted before display
  // Also filters by organization for multi-tenancy
  const setDecryptedData = useCallback(async (fetchedData) => {
    try {
      const decryptedData = await decryptDocuments(fetchedData);
      // Filter by organization using smart inference for legacy documents
      const filteredData = decryptedData.filter(doc => matchesOrganization(doc, organization));
      setData(filteredData);
    } catch (error) {
      console.error('Error decrypting data:', error);
      // Fall back to encrypted data if decryption fails, but still filter by organization
      const filteredData = fetchedData.filter(doc => matchesOrganization(doc, organization));
      setData(filteredData);
    }
  }, [organization]);

  // All complication subtypes for fetching data
  const complicationSubtypes = useMemo(() => [
    { label: 'Complication - Anesthesia', collection: 'Complications', subCollection: 'Anesthesia' },
    { label: 'Complication - Dental Implant', collection: 'Complications', subCollection: 'Dental Implant' },
    { label: 'Complication - Dentoalveolar Surgery', collection: 'Complications', subCollection: 'Dentoalveolar Surgery' },
    { label: 'Complication - Needle Stick Injury', collection: 'Complications', subCollection: 'Needle Stick Injury' },
  ], []);

  // Equipment subtypes
  const equipmentSubtypes = useMemo(() => [
    { label: 'Equipment - Surgical', collection: 'Operations', subCollection: 'Equipment___Surgical' },
    { label: 'Equipment - Imaging', collection: 'Operations', subCollection: 'Equipment___Imaging' },
    { label: 'Equipment - Sterilization', collection: 'Operations', subCollection: 'Equipment___Sterilization' },
    { label: 'Equipment - Anesthesia', collection: 'Operations', subCollection: 'Equipment___Anesthesia' },
    { label: 'Equipment - Computer/Software', collection: 'Operations', subCollection: 'Equipment___Computer_Software' },
    { label: 'Equipment - Facility Systems', collection: 'Operations', subCollection: 'Equipment___Facility_Systems' },
  ], []);

  // Complaint subtypes
  const complaintSubtypes = useMemo(() => [
    { label: 'Complaint - Clinical Care', collection: 'Operations', subCollection: 'Complaint___Clinical_Care' },
    { label: 'Complaint - Customer Service', collection: 'Operations', subCollection: 'Complaint___Customer_Service' },
    { label: 'Complaint - Billing/Financial', collection: 'Operations', subCollection: 'Complaint___Billing_Financial' },
    { label: 'Complaint - Facility/Environment', collection: 'Operations', subCollection: 'Complaint___Facility_Environment' },
  ], []);

  // Scheduling subtypes
  const schedulingSubtypes = useMemo(() => [
    { label: 'Scheduling - Appointment', collection: 'Operations', subCollection: 'Scheduling___Appointment' },
    { label: 'Scheduling - Communication', collection: 'Operations', subCollection: 'Scheduling___Communication' },
    { label: 'Scheduling - Documentation', collection: 'Operations', subCollection: 'Scheduling___Documentation' },
    { label: 'Scheduling - System/Process', collection: 'Operations', subCollection: 'Scheduling___System_Process' },
  ], []);

  // IT Breach subtypes
  const itBreachSubtypes = useMemo(() => [
    { label: 'IT - Data Event', collection: 'ITBreach', subCollection: 'DataBreach' },
    { label: 'IT - Ransomware Event', collection: 'ITBreach', subCollection: 'RansomwareAttack' },
    { label: 'IT - Cyber Event', collection: 'ITBreach', subCollection: 'CyberAttack' },
    { label: 'IT - System Event', collection: 'ITBreach', subCollection: 'SystemBreach' },
    { label: 'IT - Email Event', collection: 'ITBreach', subCollection: 'EmailBreach' },
    { label: 'IT - Device Event', collection: 'ITBreach', subCollection: 'DeviceBreach' },
  ], []);

  // HIPAA Disclosure subtypes
  const hipaaSubtypes = useMemo(() => [
    { label: 'HIPAA - Unauthorized Disclosure', collection: 'HIPAADisclosure', subCollection: 'UnauthorizedDisclosure' },
    { label: 'HIPAA - Improper Access', collection: 'HIPAADisclosure', subCollection: 'ImproperAccess' },
    { label: 'HIPAA - Lost/Misplaced Records', collection: 'HIPAADisclosure', subCollection: 'LostRecords' },
    { label: 'HIPAA - Vendor/BAA Breach', collection: 'HIPAADisclosure', subCollection: 'VendorBreach' },
    { label: 'HIPAA - Patient Rights Violation', collection: 'HIPAADisclosure', subCollection: 'PatientRights' },
    { label: 'HIPAA - Transmission Error', collection: 'HIPAADisclosure', subCollection: 'TransmissionError' },
  ], []);

  // Map item type or currentView to Firestore doc path for delete
  const getDocRefForDelete = useCallback((item, view) => {
    const id = item?.id;
    if (!id) return null;
    const type = item?.type || '';
    // Type-based path (from fetchAllData, fetchComplicationsOnly, etc.)
    if (type === 'Emergency') return doc(db, 'Emergency', id);
    if (type === 'Aggressive Patient Interaction') return doc(db, 'AggressivePatientInteractions', 'root', 'Entries', id);
    if (type.startsWith('Complication - ')) {
      const sub = type.replace('Complication - ', '');
      return doc(db, 'Complications', 'formRefDoc', sub, id);
    }
    const equipmentSubMap = { 'Equipment - Surgical': 'Equipment___Surgical', 'Equipment - Imaging': 'Equipment___Imaging', 'Equipment - Sterilization': 'Equipment___Sterilization', 'Equipment - Anesthesia': 'Equipment___Anesthesia', 'Equipment - Computer/Software': 'Equipment___Computer_Software', 'Equipment - Facility Systems': 'Equipment___Facility_Systems' };
    if (equipmentSubMap[type]) return doc(db, 'Operations', 'opsRefDoc', equipmentSubMap[type], id);
    const complaintSubMap = { 'Complaint - Clinical Care': 'Complaint___Clinical_Care', 'Complaint - Customer Service': 'Complaint___Customer_Service', 'Complaint - Billing/Financial': 'Complaint___Billing_Financial', 'Complaint - Facility/Environment': 'Complaint___Facility_Environment' };
    if (complaintSubMap[type]) return doc(db, 'Operations', 'opsRefDoc', complaintSubMap[type], id);
    const schedulingSubMap = { 'Scheduling - Appointment': 'Scheduling___Appointment', 'Scheduling - Communication': 'Scheduling___Communication', 'Scheduling - Documentation': 'Scheduling___Documentation', 'Scheduling - System/Process': 'Scheduling___System_Process' };
    if (schedulingSubMap[type]) return doc(db, 'Operations', 'opsRefDoc', schedulingSubMap[type], id);
    if (type.startsWith('IT - ')) {
      const subMap = { 'Data Event': 'DataBreach', 'Ransomware Event': 'RansomwareAttack', 'Cyber Event': 'CyberAttack', 'System Event': 'SystemBreach', 'Email Event': 'EmailBreach', 'Device Event': 'DeviceBreach' };
      const sub = subMap[type.replace('IT - ', '')] || 'DataBreach';
      return doc(db, 'ITBreach', 'root', sub, id);
    }
    if (type.startsWith('HIPAA - ')) {
      const subMap = { 'Unauthorized Disclosure': 'UnauthorizedDisclosure', 'Improper Access': 'ImproperAccess', 'Lost/Misplaced Records': 'LostRecords', 'Vendor/BAA Breach': 'VendorBreach', 'Patient Rights Violation': 'PatientRights', 'Transmission Error': 'TransmissionError' };
      const sub = subMap[type.replace('HIPAA - ', '')] || 'UnauthorizedDisclosure';
      return doc(db, 'HIPAADisclosure', 'root', sub, id);
    }
    // Fallback: derive from currentView (single-collection fetch)
    if (view === 'Emergency') return doc(db, 'Emergency', id);
    if (view && view.startsWith('Complications - ')) {
      const sub = view.replace('Complications - ', '');
      return doc(db, 'Complications', 'formRefDoc', sub, id);
    }
    if (view && view.startsWith('Operations - ')) {
      const sub = view.replace('Operations - ', '');
      return doc(db, 'Operations', 'opsRefDoc', sub, id);
    }
    if (view === 'AggressivePatientInteractions - Entries') return doc(db, 'AggressivePatientInteractions', 'root', 'Entries', id);
    if (view && view.startsWith('ITBreach - ')) return doc(db, 'ITBreach', 'root', view.replace('ITBreach - ', ''), id);
    if (view && view.startsWith('HIPAADisclosure - ')) return doc(db, 'HIPAADisclosure', 'root', view.replace('HIPAADisclosure - ', ''), id);
    if (view && view.startsWith('Success - ')) {
      const sub = view.replace('Success - ', '');
      return doc(db, 'Success', 'successRefDoc', sub, id);
    }
    return null;
  }, []);

  const clinicalButtons = useMemo(() => ([
    { label: 'Emergency', collection: 'Emergency', subCollection: '', style: 'red', icon: FaExclamationCircle, color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
    { label: 'Complications', collection: 'Complications', subCollection: 'ALL', style: 'red', icon: FaExclamationTriangle, color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
  ]), []);

  const safetyButtons = useMemo(() => ([
    { label: 'Aggressive Patient Interaction', collection: 'AggressivePatientInteractions', subCollection: 'Entries', style: 'safety', icon: FaShieldAlt, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
    { label: 'IT Security Event', collection: 'ITBreach', subCollection: 'ALL', style: 'safety', icon: FaLock, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
    { label: 'HIPAA Disclosure', collection: 'HIPAADisclosure', subCollection: 'ALL', style: 'safety', icon: FaFileMedical, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' },
  ]), []);

  const operationsButtons = useMemo(() => ([
    { label: 'Equipment', collection: 'Operations', subCollection: 'Equipment___ALL', style: 'ops', icon: FaTools, color: 'text-emerald-600', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' },
    { label: 'Complaint', collection: 'Operations', subCollection: 'Complaint___ALL', style: 'ops', icon: FaComments, color: 'text-emerald-600', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' },
    { label: 'Scheduling', collection: 'Operations', subCollection: 'Scheduling___ALL', style: 'ops', icon: FaCalendarAlt, color: 'text-emerald-600', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' },
  ]), []);

  const pillButtons = useMemo(() => ([
    ...clinicalButtons
  ]), [clinicalButtons]);

  // userRole is now passed as a prop from App.js - no need to fetch it here

  const complicationCountsRef = useRef({});
  const equipmentCountsRef = useRef({});
  const complaintCountsRef = useRef({});
  const schedulingCountsRef = useRef({});
  const itBreachCountsRef = useRef({});
  const hipaaCountsRef = useRef({});

  // Helper function to count documents filtered by organization (multi-tenancy)
  // Uses smart inference for legacy documents without organization field
  const countByOrganization = useCallback((querySnapshot) => {
    return querySnapshot.docs.filter(doc => matchesOrganization(doc.data(), organization)).length;
  }, [organization]);

  // Fetch all counts ONCE on mount
  // Always filter client-side by organization to handle legacy data without organization field
  const fetchAllCountsOnce = useCallback(async () => {
    const shouldFilterByPractice = userRole !== 'System Administrator' && userPractice;
    
    // Helper to get count - always filters client-side by organization
    const getCount = async (q) => {
      const snap = await getDocs(q);
      return snap.docs.filter(d => matchesOrganization(d.data(), organization)).length;
    };

    try {
      // Build all query promises in parallel for speed
      const queryPromises = [];
      const queryLabels = [];

      // === EMERGENCY ===
      const emergencyRef = collection(db, 'Emergency');
      const emergencyQ = shouldFilterByPractice 
        ? query(emergencyRef, where("practice", "==", userPractice))
        : emergencyRef;
      queryPromises.push(getCount(emergencyQ));
      queryLabels.push('Emergency');

      // === COMPLICATIONS (all subtypes in parallel) ===
      for (const subtype of complicationSubtypes) {
        const formRefDoc = doc(db, 'Complications', 'formRefDoc');
        const subRef = collection(formRefDoc, subtype.subCollection);
        const q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        queryPromises.push(getCount(q));
        queryLabels.push(`Complication_${subtype.subCollection}`);
      }

      // === AGGRESSIVE PATIENT ===
      const aggressiveRef = doc(db, 'AggressivePatientInteractions', 'root');
      const aggressiveSubRef = collection(aggressiveRef, 'Entries');
      const aggressiveQ = shouldFilterByPractice 
        ? query(aggressiveSubRef, where("practice", "==", userPractice))
        : aggressiveSubRef;
      queryPromises.push(getCount(aggressiveQ));
      queryLabels.push('Aggressive Patient Interaction');

      // === IT SECURITY (all subtypes in parallel) ===
      for (const subtype of itBreachSubtypes) {
        const parentRef = doc(db, 'ITBreach', 'root');
        const subRef = collection(parentRef, subtype.subCollection);
        const q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        queryPromises.push(getCount(q));
        queryLabels.push(`IT_${subtype.subCollection}`);
      }

      // === HIPAA (all subtypes in parallel) ===
      for (const subtype of hipaaSubtypes) {
        const parentRef = doc(db, 'HIPAADisclosure', 'root');
        const subRef = collection(parentRef, subtype.subCollection);
        const q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        queryPromises.push(getCount(q));
        queryLabels.push(`HIPAA_${subtype.subCollection}`);
      }

      // === EQUIPMENT (all subtypes in parallel) ===
      for (const subtype of equipmentSubtypes) {
        const parentRef = doc(db, 'Operations', 'opsRefDoc');
        const subRef = collection(parentRef, subtype.subCollection);
        const q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        queryPromises.push(getCount(q));
        queryLabels.push(`Equipment_${subtype.subCollection}`);
      }

      // === COMPLAINT (all subtypes in parallel) ===
      for (const subtype of complaintSubtypes) {
        const parentRef = doc(db, 'Operations', 'opsRefDoc');
        const subRef = collection(parentRef, subtype.subCollection);
        const q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        queryPromises.push(getCount(q));
        queryLabels.push(`Complaint_${subtype.subCollection}`);
      }

      // === SCHEDULING (all subtypes in parallel) ===
      for (const subtype of schedulingSubtypes) {
        const parentRef = doc(db, 'Operations', 'opsRefDoc');
        const subRef = collection(parentRef, subtype.subCollection);
        const q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        queryPromises.push(getCount(q));
        queryLabels.push(`Scheduling_${subtype.subCollection}`);
      }

      // Execute all queries in parallel
      const results = await Promise.all(queryPromises.map(p => p.catch(e => {
        console.error('Count query error:', e);
        return 0;
      })));

      // Aggregate results by category
      const counts = {
        'Emergency': 0,
        'Complications': 0,
        'Aggressive Patient Interaction': 0,
        'IT Security Event': 0,
        'HIPAA Disclosure': 0,
        'Equipment': 0,
        'Complaint': 0,
        'Scheduling': 0
      };

      results.forEach((count, idx) => {
        const label = queryLabels[idx];
        if (label === 'Emergency') counts['Emergency'] = count;
        else if (label === 'Aggressive Patient Interaction') counts['Aggressive Patient Interaction'] = count;
        else if (label.startsWith('Complication_')) counts['Complications'] += count;
        else if (label.startsWith('IT_')) counts['IT Security Event'] += count;
        else if (label.startsWith('HIPAA_')) counts['HIPAA Disclosure'] += count;
        else if (label.startsWith('Equipment_')) counts['Equipment'] += count;
        else if (label.startsWith('Complaint_')) counts['Complaint'] += count;
        else if (label.startsWith('Scheduling_')) counts['Scheduling'] += count;
      });

      setButtonCounts(counts);

    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  }, [userPractice, userRole, organization, complicationSubtypes, equipmentSubtypes, complaintSubtypes, schedulingSubtypes, itBreachSubtypes, hipaaSubtypes]);
  
  // Fetch counts once on mount (no real-time listeners to reduce Firestore reads)
  useEffect(() => {
    if (userRole === 'System Administrator' || userPractice) {
      fetchAllCountsOnce();
    }
  }, [userPractice, userRole, fetchAllCountsOnce]);
  

  // Fetch data using getDocs (one-time fetch) instead of onSnapshot (real-time listeners)
  // This dramatically reduces Firestore reads and network requests
  const fetchData = useCallback(async (collectionName, subCollectionName = '') => {
    // Clean up any previous listeners (for legacy compatibility)
    if (activeListenersCleanupRef.current) {
      activeListenersCleanupRef.current();
      activeListenersCleanupRef.current = null;
    }
  
    setLoading(true);
    setCurrentView(subCollectionName ? `${collectionName} - ${subCollectionName}` : collectionName);
  
    try {
      const shouldFilterByPractice = userRole !== 'System Administrator' && userPractice;
      let q;
      
      if (collectionName === 'Success') {
        const successRefDoc = doc(db, 'Success', 'successRefDoc');
        const subCollectionRef = collection(successRefDoc, subCollectionName);
        q = shouldFilterByPractice 
          ? query(subCollectionRef, where("practice", "==", userPractice))
          : subCollectionRef;
        const querySnapshot = await getDocs(q);
        const fetchedData = querySnapshot.docs.map(d => ({
          id: d.id,
          type: `Success - ${subCollectionName}`,
          ...d.data()
        }));
        await setDecryptedData(fetchedData);
      } else if (collectionName === 'Complications') {
        const formRefDoc = doc(db, 'Complications', 'formRefDoc');
        const subCollectionRef = collection(formRefDoc, subCollectionName);
        q = shouldFilterByPractice 
          ? query(subCollectionRef, where("practice", "==", userPractice))
          : subCollectionRef;
        const querySnapshot = await getDocs(q);
        const fetchedData = querySnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        await setDecryptedData(fetchedData);
      } else if (collectionName === 'Operations') {
        const parentRef = doc(db, 'Operations', 'opsRefDoc');
        const subRef = collection(parentRef, subCollectionName);
        q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        const querySnapshot = await getDocs(q);
        const fetchedData = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        await setDecryptedData(fetchedData);
      } else if (collectionName === 'AggressivePatientInteractions') {
        const parentRef = doc(db, 'AggressivePatientInteractions', 'root');
        const subRef = collection(parentRef, subCollectionName);
        q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        const querySnapshot = await getDocs(q);
        const fetchedData = querySnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        await setDecryptedData(fetchedData);
      } else if (collectionName === 'ITBreach') {
        const parentRef = doc(db, 'ITBreach', 'root');
        const subRef = collection(parentRef, subCollectionName);
        q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        const querySnapshot = await getDocs(q);
        const fetchedData = querySnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        await setDecryptedData(fetchedData);
      } else if (collectionName === 'HIPAADisclosure') {
        const parentRef = doc(db, 'HIPAADisclosure', 'root');
        const subRef = collection(parentRef, subCollectionName);
        q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        const querySnapshot = await getDocs(q);
        const fetchedData = querySnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        await setDecryptedData(fetchedData);
      } else if (collectionName === 'Emergency') {
        const collectionRef = collection(db, collectionName);
        q = shouldFilterByPractice 
          ? query(collectionRef, where("practice", "==", userPractice))
          : collectionRef;
        const querySnapshot = await getDocs(q);
        const fetchedData = querySnapshot.docs.map(d => ({
          id: d.id,
          type: 'Emergency',
          ...d.data()
        }));
        await setDecryptedData(fetchedData);
      } else {
        const collectionRef = collection(db, collectionName);
        q = shouldFilterByPractice 
          ? query(collectionRef, where("practice", "==", userPractice))
          : collectionRef;
        const querySnapshot = await getDocs(q);
        const fetchedData = querySnapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        await setDecryptedData(fetchedData);
      }
  
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  }, [userPractice, userRole, setDecryptedData]);
  

  // Fetch all data from all collections using getDocs (one-time fetch)
  // This dramatically reduces network requests compared to onSnapshot listeners
  const fetchAllData = useCallback(async () => {
    // Clean up any previous listeners (for legacy compatibility)
    if (activeListenersCleanupRef.current) {
      activeListenersCleanupRef.current();
      activeListenersCleanupRef.current = null;
    }

    setLoading(true);
    setCurrentView('All Records');
    setData([]);

    try {
      const shouldFilterByPractice = userRole !== 'System Administrator' && userPractice;
      const allData = [];

      // Helper function to get time for sorting
      const getTime = (val) => {
        if (!val) return 0;
        if (val.toDate) return val.toDate().getTime();
        if (val instanceof Date) return val.getTime();
        return new Date(val).getTime() || 0;
      };

      // === EMERGENCY ===
      try {
        const emergencyRef = collection(db, 'Emergency');
        const q = shouldFilterByPractice 
          ? query(emergencyRef, where("practice", "==", userPractice))
          : emergencyRef;
        const snap = await getDocs(q);
        snap.docs.forEach(d => {
          allData.push({ id: d.id, type: 'Emergency', ...d.data() });
        });
      } catch (error) {
        console.error('Error fetching Emergency:', error);
      }

      // === COMPLICATIONS (all subtypes) ===
      for (const subtype of complicationSubtypes) {
        try {
          const formRefDoc = doc(db, 'Complications', 'formRefDoc');
          const subRef = collection(formRefDoc, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subRef, where("practice", "==", userPractice))
            : subRef;
          const snap = await getDocs(q);
          snap.docs.forEach(d => {
            allData.push({ id: d.id, type: subtype.label, ...d.data() });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      // === AGGRESSIVE PATIENT ===
      try {
        const parentRef = doc(db, 'AggressivePatientInteractions', 'root');
        const subRef = collection(parentRef, 'Entries');
        const q = shouldFilterByPractice 
          ? query(subRef, where("practice", "==", userPractice))
          : subRef;
        const snap = await getDocs(q);
        snap.docs.forEach(d => {
          allData.push({ id: d.id, type: 'Aggressive Patient Interaction', ...d.data() });
        });
      } catch (error) {
        console.error('Error fetching Aggressive Patient:', error);
      }

      // === IT BREACH (all subtypes) ===
      for (const subtype of itBreachSubtypes) {
        try {
          const parentRef = doc(db, 'ITBreach', 'root');
          const subRef = collection(parentRef, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subRef, where("practice", "==", userPractice))
            : subRef;
          const snap = await getDocs(q);
          snap.docs.forEach(d => {
            allData.push({ id: d.id, type: subtype.label, ...d.data() });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      // === HIPAA (all subtypes) ===
      for (const subtype of hipaaSubtypes) {
        try {
          const parentRef = doc(db, 'HIPAADisclosure', 'root');
          const subRef = collection(parentRef, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subRef, where("practice", "==", userPractice))
            : subRef;
          const snap = await getDocs(q);
          snap.docs.forEach(d => {
            allData.push({ id: d.id, type: subtype.label, ...d.data() });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      // === EQUIPMENT (all subtypes) ===
      for (const subtype of equipmentSubtypes) {
        try {
          const parentRef = doc(db, 'Operations', 'opsRefDoc');
          const subRef = collection(parentRef, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subRef, where("practice", "==", userPractice))
            : subRef;
          const snap = await getDocs(q);
          snap.docs.forEach(d => {
            allData.push({ id: d.id, type: subtype.label, ...d.data() });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      // === COMPLAINT (all subtypes) ===
      for (const subtype of complaintSubtypes) {
        try {
          const parentRef = doc(db, 'Operations', 'opsRefDoc');
          const subRef = collection(parentRef, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subRef, where("practice", "==", userPractice))
            : subRef;
          const snap = await getDocs(q);
          snap.docs.forEach(d => {
            allData.push({ id: d.id, type: subtype.label, ...d.data() });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      // === SCHEDULING (all subtypes) ===
      for (const subtype of schedulingSubtypes) {
        try {
          const parentRef = doc(db, 'Operations', 'opsRefDoc');
          const subRef = collection(parentRef, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subRef, where("practice", "==", userPractice))
            : subRef;
          const snap = await getDocs(q);
          snap.docs.forEach(d => {
            allData.push({ id: d.id, type: subtype.label, ...d.data() });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      // Sort by submittedAt (newest first)
      allData.sort((a, b) => getTime(b.submittedAt) - getTime(a.submittedAt));

      // Decrypt PHI fields before displaying
      await setDecryptedData(allData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching all data:", error);
      setLoading(false);
    }
  }, [userPractice, userRole, complicationSubtypes, equipmentSubtypes, complaintSubtypes, schedulingSubtypes, itBreachSubtypes, hipaaSubtypes, setDecryptedData]);

  // Fetch only complications data
  const fetchComplicationsOnly = useCallback(async () => {
    // Clean up any previous listeners
    if (activeListenersCleanupRef.current) {
      activeListenersCleanupRef.current();
      activeListenersCleanupRef.current = null;
    }

    setLoading(true);
    setCurrentView('Complications');
    setData([]); // Clear existing data

    try {
      const shouldFilterByPractice = userRole !== 'System Administrator' && userPractice;
      const allData = [];

      // Fetch data from each complication subtype using getDocs for reliability
      for (const subtype of complicationSubtypes) {
        try {
          const formRefDoc = doc(db, 'Complications', 'formRefDoc');
          const subCollectionRef = collection(formRefDoc, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subCollectionRef, where("practice", "==", userPractice))
            : subCollectionRef;
          
          const querySnapshot = await getDocs(q);
          
          querySnapshot.docs.forEach(docSnapshot => {
            allData.push({
              id: docSnapshot.id,
              type: subtype.label,
              ...docSnapshot.data()
            });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      // Sort by submittedAt (newest first)
      allData.sort((a, b) => {
        const getTime = (val) => {
          if (!val) return 0;
          if (val.toDate) return val.toDate().getTime();
          if (val instanceof Date) return val.getTime();
          return new Date(val).getTime() || 0;
        };
        return getTime(b.submittedAt) - getTime(a.submittedAt);
      });

      // Decrypt PHI fields before displaying
      await setDecryptedData(allData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching complications data:", error);
      setLoading(false);
    }
  }, [userPractice, userRole, complicationSubtypes, setDecryptedData]);

  // Fetch only equipment data
  const fetchEquipmentOnly = useCallback(async () => {
    // Clean up any previous listeners
    if (activeListenersCleanupRef.current) {
      activeListenersCleanupRef.current();
      activeListenersCleanupRef.current = null;
    }

    setLoading(true);
    setCurrentView('Equipment');
    setData([]);

    try {
      const shouldFilterByPractice = userRole !== 'System Administrator' && userPractice;
      const allData = [];

      for (const subtype of equipmentSubtypes) {
        try {
          const parentRef = doc(db, 'Operations', 'opsRefDoc');
          const subRef = collection(parentRef, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subRef, where("practice", "==", userPractice))
            : subRef;
          
          const querySnapshot = await getDocs(q);
          
          querySnapshot.docs.forEach(docSnapshot => {
            allData.push({
              id: docSnapshot.id,
              type: subtype.label,
              ...docSnapshot.data()
            });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      allData.sort((a, b) => {
        const getTime = (val) => {
          if (!val) return 0;
          if (val.toDate) return val.toDate().getTime();
          if (val instanceof Date) return val.getTime();
          return new Date(val).getTime() || 0;
        };
        return getTime(b.submittedAt) - getTime(a.submittedAt);
      });

      await setDecryptedData(allData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching equipment data:", error);
      setLoading(false);
    }
  }, [userPractice, userRole, equipmentSubtypes, setDecryptedData]);

  // Fetch only complaint data
  const fetchComplaintOnly = useCallback(async () => {
    // Clean up any previous listeners
    if (activeListenersCleanupRef.current) {
      activeListenersCleanupRef.current();
      activeListenersCleanupRef.current = null;
    }

    setLoading(true);
    setCurrentView('Complaint');
    setData([]);

    try {
      const shouldFilterByPractice = userRole !== 'System Administrator' && userPractice;
      const allData = [];

      for (const subtype of complaintSubtypes) {
        try {
          const parentRef = doc(db, 'Operations', 'opsRefDoc');
          const subRef = collection(parentRef, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subRef, where("practice", "==", userPractice))
            : subRef;
          
          const querySnapshot = await getDocs(q);
          
          querySnapshot.docs.forEach(docSnapshot => {
            allData.push({
              id: docSnapshot.id,
              type: subtype.label,
              ...docSnapshot.data()
            });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      allData.sort((a, b) => {
        const getTime = (val) => {
          if (!val) return 0;
          if (val.toDate) return val.toDate().getTime();
          if (val instanceof Date) return val.getTime();
          return new Date(val).getTime() || 0;
        };
        return getTime(b.submittedAt) - getTime(a.submittedAt);
      });

      await setDecryptedData(allData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching complaint data:", error);
      setLoading(false);
    }
  }, [userPractice, userRole, complaintSubtypes, setDecryptedData]);

  // Fetch only scheduling data
  const fetchSchedulingOnly = useCallback(async () => {
    // Clean up any previous listeners
    if (activeListenersCleanupRef.current) {
      activeListenersCleanupRef.current();
      activeListenersCleanupRef.current = null;
    }

    setLoading(true);
    setCurrentView('Scheduling');
    setData([]);

    try {
      const shouldFilterByPractice = userRole !== 'System Administrator' && userPractice;
      const allData = [];

      for (const subtype of schedulingSubtypes) {
        try {
          const parentRef = doc(db, 'Operations', 'opsRefDoc');
          const subRef = collection(parentRef, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subRef, where("practice", "==", userPractice))
            : subRef;
          
          const querySnapshot = await getDocs(q);
          
          querySnapshot.docs.forEach(docSnapshot => {
            allData.push({
              id: docSnapshot.id,
              type: subtype.label,
              ...docSnapshot.data()
            });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      allData.sort((a, b) => {
        const getTime = (val) => {
          if (!val) return 0;
          if (val.toDate) return val.toDate().getTime();
          if (val instanceof Date) return val.getTime();
          return new Date(val).getTime() || 0;
        };
        return getTime(b.submittedAt) - getTime(a.submittedAt);
      });

      await setDecryptedData(allData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching scheduling data:", error);
      setLoading(false);
    }
  }, [userPractice, userRole, schedulingSubtypes, setDecryptedData]);

  // Fetch only IT Breach data
  const fetchITBreachOnly = useCallback(async () => {
    // Clean up any previous listeners
    if (activeListenersCleanupRef.current) {
      activeListenersCleanupRef.current();
      activeListenersCleanupRef.current = null;
    }
    
    setLoading(true);
    setCurrentView('IT Security Event');
    setData([]);

    try {
      const shouldFilterByPractice = userRole !== 'System Administrator' && userPractice;
      const allData = [];

      // Fetch data from each IT breach subtype using getDocs for reliability
      for (const subtype of itBreachSubtypes) {
        try {
          const parentRef = doc(db, 'ITBreach', 'root');
          const subRef = collection(parentRef, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subRef, where("practice", "==", userPractice))
            : subRef;
          
          const querySnapshot = await getDocs(q);
          
          querySnapshot.docs.forEach(docSnapshot => {
            allData.push({
              id: docSnapshot.id,
              type: subtype.label,
              ...docSnapshot.data()
            });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      // Sort by submittedAt (newest first)
      allData.sort((a, b) => {
        const getTime = (val) => {
          if (!val) return 0;
          if (val.toDate) return val.toDate().getTime(); // Firestore Timestamp
          if (val instanceof Date) return val.getTime();
          return new Date(val).getTime() || 0;
        };
        return getTime(b.submittedAt) - getTime(a.submittedAt);
      });

      // Decrypt PHI fields before displaying
      await setDecryptedData(allData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching IT Breach data:", error);
      setLoading(false);
    }
  }, [userPractice, userRole, itBreachSubtypes, setDecryptedData]);

  // Fetch only HIPAA Disclosure data
  const fetchHIPAADisclosureOnly = useCallback(async () => {
    // Clean up any previous listeners
    if (activeListenersCleanupRef.current) {
      activeListenersCleanupRef.current();
      activeListenersCleanupRef.current = null;
    }
    
    setLoading(true);
    setCurrentView('HIPAA Disclosure');
    setData([]);

    try {
      const shouldFilterByPractice = userRole !== 'System Administrator' && userPractice;
      const allData = [];

      // Fetch data from each HIPAA disclosure subtype using getDocs for reliability
      for (const subtype of hipaaSubtypes) {
        try {
          const parentRef = doc(db, 'HIPAADisclosure', 'root');
          const subRef = collection(parentRef, subtype.subCollection);
          const q = shouldFilterByPractice 
            ? query(subRef, where("practice", "==", userPractice))
            : subRef;
          
          const querySnapshot = await getDocs(q);
          
          querySnapshot.docs.forEach(docSnapshot => {
            allData.push({
              id: docSnapshot.id,
              type: subtype.label,
              ...docSnapshot.data()
            });
          });
        } catch (error) {
          console.error(`Error fetching ${subtype.label}:`, error);
        }
      }

      // Sort by submittedAt (newest first)
      allData.sort((a, b) => {
        const getTime = (val) => {
          if (!val) return 0;
          if (val.toDate) return val.toDate().getTime(); // Firestore Timestamp
          if (val instanceof Date) return val.getTime();
          return new Date(val).getTime() || 0;
        };
        return getTime(b.submittedAt) - getTime(a.submittedAt);
      });

      // Decrypt PHI fields before displaying
      await setDecryptedData(allData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching HIPAA Disclosure data:", error);
      setLoading(false);
    }
  }, [userPractice, userRole, hipaaSubtypes, setDecryptedData]);

  const handleView = useCallback((collectionName, subCollectionName = '', label = '') => {
    lastFetchParamsRef.current = { collection: collectionName, subCollection: subCollectionName, label };
    // Route to appropriate fetch function based on clicked card
    if (label === 'Complications' || (collectionName === 'Complications' && subCollectionName === 'ALL')) {
      fetchComplicationsOnly();
    } else if (label === 'Equipment' || (collectionName === 'Operations' && subCollectionName === 'Equipment___ALL')) {
      fetchEquipmentOnly();
    } else if (label === 'Complaint' || (collectionName === 'Operations' && subCollectionName === 'Complaint___ALL')) {
      fetchComplaintOnly();
    } else if (label === 'Scheduling' || (collectionName === 'Operations' && subCollectionName === 'Scheduling___ALL')) {
      fetchSchedulingOnly();
    } else if (label === 'IT Security Event' || (collectionName === 'ITBreach' && subCollectionName === 'ALL')) {
      fetchITBreachOnly();
    } else if (label === 'HIPAA Disclosure' || (collectionName === 'HIPAADisclosure' && subCollectionName === 'ALL')) {
      fetchHIPAADisclosureOnly();
    } else if (collectionName === 'Success' || label.includes('Success')) {
      // For Success records, fetch only that specific subcollection
    fetchData(collectionName, subCollectionName);
    } else if (collectionName === 'Emergency' || label === 'Emergency') {
      // For Emergency records, fetch only that collection
      fetchData(collectionName, subCollectionName);
    } else if (collectionName === 'AggressivePatientInteractions' || label === 'Aggressive Patient Interaction') {
      // For Aggressive Patient Interaction records, fetch only that collection
      fetchData(collectionName, subCollectionName);
    } else {
      // Otherwise fetch all data
      fetchAllData();
    }
  }, [fetchAllData, fetchComplicationsOnly, fetchEquipmentOnly, fetchComplaintOnly, fetchSchedulingOnly, fetchITBreachOnly, fetchHIPAADisclosureOnly, fetchData]);

  const refreshCurrentData = useCallback(() => {
    const { collection: col, subCollection: sub, label } = lastFetchParamsRef.current;
    if (label === 'Complications' || (col === 'Complications' && sub === 'ALL')) fetchComplicationsOnly();
    else if (label === 'Equipment' || (col === 'Operations' && sub === 'Equipment___ALL')) fetchEquipmentOnly();
    else if (label === 'Complaint' || (col === 'Operations' && sub === 'Complaint___ALL')) fetchComplaintOnly();
    else if (label === 'Scheduling' || (col === 'Operations' && sub === 'Scheduling___ALL')) fetchSchedulingOnly();
    else if (label === 'IT Security Event' || (col === 'ITBreach' && sub === 'ALL')) fetchITBreachOnly();
    else if (label === 'HIPAA Disclosure' || (col === 'HIPAADisclosure' && sub === 'ALL')) fetchHIPAADisclosureOnly();
    else if (col === 'Success' || (label && label.includes('Success'))) fetchData(col, sub);
    else if (col === 'Emergency' || label === 'Emergency') fetchData(col, sub);
    else if (col === 'AggressivePatientInteractions' || label === 'Aggressive Patient Interaction') fetchData(col, sub);
    else fetchAllData();
  }, [fetchComplicationsOnly, fetchEquipmentOnly, fetchComplaintOnly, fetchSchedulingOnly, fetchITBreachOnly, fetchHIPAADisclosureOnly, fetchData, fetchAllData]);

  const handleDeleteClick = useCallback((item) => {
    setDeleteConfirmItem(item);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmItem) return;
    const docRef = getDocRefForDelete(deleteConfirmItem, currentView);
    if (!docRef) {
      toast.error('Cannot determine document location for deletion.');
      setDeleteConfirmItem(null);
      return;
    }
    setIsDeleting(true);
    try {
      await deleteDoc(docRef);
      toast.success('Entry removed successfully.');
      setDeleteConfirmItem(null);
      refreshCurrentData();
      fetchAllCountsOnce();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error(toUserMessage(error, 'Failed to remove that entry. Please try again.'));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteConfirmItem, currentView, getDocRefForDelete, refreshCurrentData, fetchAllCountsOnce]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmItem(null);
  }, []);

  // Get all available fields from data (excluding 'id')
  const availableFields = useMemo(() => {
    if (data.length === 0) return [];
    const fields = new Set();
    // Fields to exclude from display (internal metadata, deprecated fields)
    const excludeFields = ['id', '_encrypted', 'completionDate'];
    data.forEach(item => {
      Object.keys(item).forEach(key => {
        if (!excludeFields.includes(key)) {
          fields.add(key);
        }
      });
    });
    return Array.from(fields).sort();
  }, [data]);

  // Get unique values for a specific field
  const getUniqueValuesForField = useCallback((field) => {
    const values = new Set();
    data.forEach(item => {
      const value = item[field];
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => values.add(String(v)));
        } else {
          values.add(String(value));
        }
      }
    });
    return Array.from(values).sort();
  }, [data]);

  // Get primary fields to display in table (most common/important fields)
  const getPrimaryFields = useMemo(() => {
    if (data.length === 0) return [];
    const firstItem = data[0];
    // Exclude internal metadata fields
    const allFields = Object.keys(firstItem).filter(k => k !== 'id' && k !== '_encrypted');
    
    // Always include 'type' as the first column
    const hasType = allFields.includes('type');
    const fieldsWithoutType = allFields.filter(f => f !== 'type');
    
    // Exclude fields that should not be in header (like treatment options)
    const excludeFromHeader = ['treatmentOptions', 'treatment', 'treatmentDetails', 'fullDetails'];
    const firstItemType = firstItem?.type || '';
    if (firstItemType.includes('Complication')) excludeFromHeader.push('contextOfPlacement');
    if (firstItemType.includes('Emergency') || firstItemType.includes('Success')) excludeFromHeader.push('procedureId', 'procedureID');
    const filteredFields = fieldsWithoutType.filter(f =>
      !excludeFromHeader.some(exclude => f.toLowerCase().includes(exclude.toLowerCase()))
    );
    
    // Prioritize certain fields - ensure patientID is always high priority
    const priorityFields = ['practice', 'userEmail', 'submittedAt', 'dateOfEvent', 'patientID', 'patientId', 'equipmentName', 'description', 'location'];
    let ordered = [
      ...priorityFields.filter(f => filteredFields.includes(f)),
      ...filteredFields.filter(f => !priorityFields.includes(f))
    ];
    
    // For Emergency and Success records, ensure patientID is always shown if it exists
    const isEmergencyOrSuccess = firstItemType.includes('Emergency') || firstItemType.includes('Success');
    
    if (isEmergencyOrSuccess) {
      // Ensure patientID/patientId is in the list if it exists
      const patientIdField = filteredFields.find(f => f === 'patientID' || f === 'patientId');
      if (patientIdField && !ordered.slice(0, 5).includes(patientIdField)) {
        // If patientID exists but isn't in top 5, prioritize it
        const orderedWithoutPatientId = ordered.filter(f => f !== patientIdField);
        ordered = [patientIdField, ...orderedWithoutPatientId];
      }
    }
    
    // Return type first, then up to 5 more fields
    return hasType 
      ? ['type', ...ordered.slice(0, 5)]
      : ordered.slice(0, 6);
  }, [data]);

  // Reset filters when view changes
  useEffect(() => {
    setFilters({});
    setSelectedFilterFields(new Set());
    setPendingFilters({});
  }, [currentView]);

  // Scroll to data viewer when currentView changes
  useEffect(() => {
    if (currentView && dataViewerRef.current) {
      // Small delay to ensure the element is rendered
      setTimeout(() => {
        dataViewerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [currentView]);


  // Format phone number to (XXX) XXX-XXXX format
  const formatPhoneNumber = useCallback((value) => {
    if (!value) return value;
    
    // Convert to string and remove all non-digit characters
    const digits = String(value).replace(/\D/g, '');
    
    // Check if it's a 10-digit US phone number
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    
    // Check if it's an 11-digit number starting with 1 (US with country code)
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    
    // Return original if not a recognized phone format
    return value;
  }, []);
  
  // Format field name for display - handles special cases like "patientID" -> "Patient ID"
  const formatFieldLabel = useCallback((fieldName) => {
    // Special cases for common field names
    const specialLabels = {
      'patientID': 'Patient ID',
      'patientId': 'Patient ID',
      'userEmail': 'User Email',
      'submittedAt': 'Submitted At',
      'dateOfEvent': 'Date of Event',
      'procedureId': 'Procedure ID',
      'procedureID': 'Procedure ID',
      'equipmentName': 'Equipment Name',
      'patientPhoneNumber': 'Patient Phone',
      'patientPhone': 'Patient Phone',
      'otherInfo': 'Other Info',
    };
    
    if (specialLabels[fieldName]) {
      return specialLabels[fieldName];
    }
    
    // Default: convert camelCase to Title Case with spaces
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }, []);

  // Check if a value looks like a phone number
  const isPhoneNumber = useCallback((value, fieldName = '') => {
    // Check field name for phone-related terms
    const phoneFieldNames = ['phone', 'mobile', 'cell', 'telephone', 'fax', 'contact'];
    const isPhoneField = phoneFieldNames.some(term => 
      fieldName.toLowerCase().includes(term)
    );
    
    if (isPhoneField) return true;
    
    // Check if value looks like a phone number (10+ digits)
    if (typeof value === 'string' || typeof value === 'number') {
      const digits = String(value).replace(/\D/g, '');
      // 10 digits (US) or 11 digits starting with 1
      return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
    }
    
    return false;
  }, []);

  // Format value for display - handles arrays by stacking items, removes brackets unless they're in the actual data
  const formatValueForDisplay = useCallback((value, fieldName = '') => {
    if (value === null || value === undefined) {
      return '—';
    }
    
    // Check if this is a date/timestamp field and format it in user's timezone
    if (isDateField(fieldName) || isDateValue(value)) {
      // For date-only values (like completionDate stored as "2026-01-15"), don't show time
      if (isDateOnlyValue(value)) {
        return formatDate(value, 'medium');
      }
      // For full timestamps, show date and time
      return formatDateTime(value, { 
        includeTime: true, 
        dateStyle: 'medium' 
      });
    }
    
    // Check if this is a phone number and format it
    if (isPhoneNumber(value, fieldName)) {
      return formatPhoneNumber(value);
    }

    // Check if this is an image URL (Firebase Storage or known xray fields)
    const imageFields = ['preplacementXray', 'removalXray'];
    const isImageUrl = typeof value === 'string' && value.startsWith('http') &&
      (imageFields.includes(fieldName) ||
       /firebasestorage\.googleapis\.com|\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(value));
    if (isImageUrl) {
      return (
        <div className="flex flex-col gap-2">
          <img
            src={value}
            alt={fieldName}
            className="max-w-xs max-h-48 rounded-lg border border-gray-200 object-contain"
            loading="lazy"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-600 hover:text-indigo-800 truncate max-w-xs block"
          >
            Open in new tab
          </a>
        </div>
      );
    }
    
    // Handle arrays - stack items vertically without brackets
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '—';
      }
      
      // Process each item to handle nested arrays and character arrays
      const processedItems = value.map(item => {
        if (Array.isArray(item)) {
          // Check if it's all single characters (likely a word split into characters)
          const allSingleChars = item.every(i => typeof i === 'string' && i.length === 1);
          if (allSingleChars && item.length > 1 && item.length < 50) {
            // Rejoin characters into a word (no spaces)
            return item.join('');
          }
          // Otherwise, it's a nested array - format it
          return formatValueForDisplay(item);
        }
        return String(item);
      });
      
      // Check if all processed items are simple strings/numbers
      
      // Determine if items should be joined or stacked
      // Check if we have items that look like they belong together vs. distinct values
      const hasComplexItems = processedItems.some(item => {
        const str = String(item);
        return str.includes(' ') || str.includes('-') || str.length >= 10;
      });
      
      // If we have 2-3 items that are all simple and short, try to group them
      // But if any item looks complex (has spaces/dashes), treat each as a separate value
      if (hasComplexItems || processedItems.length > 3) {
        // Stack all items vertically - they're distinct values
        return (
          <div className="flex flex-col gap-1">
            {processedItems.map((item, index) => (
              <div key={index} className="text-sm text-gray-900">
                {item}
              </div>
            ))}
    </div>
        );
      }
      
      // For 2-3 simple items, check if they should be joined
      // Only join if all items are very short and don't contain spaces/dashes
      const shouldJoin = processedItems.length >= 2 && processedItems.length <= 3 &&
        processedItems.every(item => {
          const str = String(item);
          return str.length < 10 && !str.includes(' ') && !str.includes('-');
        });
      
      if (shouldJoin) {
        // Join small items on one line (e.g., "6 years +")
        return processedItems.join(' ');
      }
      
      // Otherwise, stack them vertically
      return (
        <div className="flex flex-col gap-1">
          {processedItems.map((item, index) => (
            <div key={index} className="text-sm text-gray-900">
              {item}
    </div>
          ))}
        </div>
      );
    }
    
    // Handle objects - format as JSON but check if it's a string that looks like JSON
    if (typeof value === 'object') {
      // Check if it's already a stringified JSON (common from Firestore)
      const stringValue = JSON.stringify(value);
      // If it's a simple object, format it nicely
      return stringValue;
    }
    
    // Handle strings - check if it looks like a JSON array string and parse it
    if (typeof value === 'string') {
      // Try to parse if it looks like JSON array
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          // Use the same logic as direct arrays
          return formatValueForDisplay(parsed);
        }
      } catch (e) {
        // Not JSON, return as-is
      }
      return value;
    }
    
    return String(value);
  }, [isPhoneNumber, formatPhoneNumber]);

  // Filter fields based on record type and relevance
  const getRelevantFields = useCallback((item) => {
    const type = item.type || '';
    const allFields = Object.keys(item).filter(k => k !== 'id');
    
    // Exclude treatment options, encryption metadata, deprecated fields, and other fields that shouldn't be displayed
    const excludeFields = ['treatmentOptions', 'treatment', 'treatmentDetails', 'fullDetails', '_encrypted', 'completionDate'];
    
    // Filter out empty/null/undefined values and excluded fields
    const nonEmptyFields = allFields.filter(key => {
      // Exclude treatment options and other excluded fields
      if (excludeFields.includes(key)) {
        return false;
      }
      const value = item[key];
      return value !== null && value !== undefined && value !== '' && 
             !(Array.isArray(value) && value.length === 0) &&
             !(typeof value === 'object' && Object.keys(value).length === 0);
    });
    
    // Medical/dental complication fields that should NOT appear in IT Breach or HIPAA records
    const medicalFields = [
      'sinusExposure', 'retainedInstrumentForeignBody', 'retainedRootTip', 'alveolarOsteitis',
      'jawFracture', 'nerveDamageType', 'damageAdjacentTeeth', 'nerveDamage', 'lipLaceration',
      'lipBurn', 'complicationType', 'dentoalveolarSurgery', 'dentalImplant', 'anesthesia',
      'needleStickInjury', 'procedureType', 'surgeryType', 'implantType', 'anesthesiaType'
    ];
    
    // IT Breach specific fields that should appear for IT Breach records
    const itBreachFields = [
      'breachType', 'dataBreach', 'ransomwareAttack', 'cyberAttack', 'systemBreach',
      'emailBreach', 'deviceBreach', 'breachDescription', 'affectedSystems', 'dataCompromised',
      'breachDate', 'detectionDate', 'reportedDate', 'incidentDescription', 'attackVector',
      'malwareType', 'ransomAmount', 'paymentMade', 'dataBackup', 'recoveryTime'
    ];
    
    // HIPAA specific fields
    const hipaaFields = [
      'disclosureType', 'unauthorizedDisclosure', 'improperAccess', 'lostRecords', 'vendorBreach',
      'patientRights', 'transmissionError', 'breachDescription', 'affectedPatients', 'dataCompromised',
      'breachDate', 'discoveryDate', 'reportedDate', 'notificationDate', 'breachLocation'
    ];
    
    // Equipment specific fields
    const equipmentFields = [
      'equipmentName', 'equipmentType', 'malfunctionType', 'equipmentLocation', 'serialNumber',
      'manufacturer', 'model', 'malfunctionDescription', 'impactOnOperations', 'replacementNeeded'
    ];
    
    // Complaint specific fields
    const complaintFields = [
      'complaintType', 'complaintDescription', 'patientName', 'complaintDate', 'resolutionStatus',
      'resolutionDate', 'followUpRequired', 'severity', 'category'
    ];
    
    // Scheduling specific fields
    const schedulingFields = [
      'appointmentDate', 'appointmentTime', 'patientName', 'schedulingError', 'errorType',
      'errorDescription', 'impact', 'resolution', 'communicationIssue', 'documentationIssue'
    ];
    
    // Filter fields based on record type
    let relevantFields = nonEmptyFields;
    
    if (type.includes('IT') || type.includes('Breach')) {
      // For IT Breach records, remove medical fields and keep only IT-relevant fields
      relevantFields = nonEmptyFields.filter(field => {
        // Include common fields (practice, userEmail, dates, etc.)
        if (['practice', 'userEmail', 'submittedAt', 'dateOfEvent', 'patientID', 'patientPhoneNumber', 'type'].includes(field)) {
          return true;
        }
        // Include IT-specific fields
        if (itBreachFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return true;
        }
        // Exclude medical fields
        if (medicalFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return false;
        }
        // For other fields, include them if they're not clearly medical
        return !medicalFields.some(f => field.toLowerCase().includes(f.toLowerCase()));
      });
    } else if (type.includes('HIPAA') || type.includes('Disclosure')) {
      // For HIPAA records, remove medical fields and keep only HIPAA-relevant fields
      relevantFields = nonEmptyFields.filter(field => {
        if (['practice', 'userEmail', 'submittedAt', 'dateOfEvent', 'patientID', 'patientPhoneNumber', 'type'].includes(field)) {
          return true;
        }
        if (hipaaFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return true;
        }
        if (medicalFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return false;
        }
        return !medicalFields.some(f => field.toLowerCase().includes(f.toLowerCase()));
      });
    } else if (type.includes('Equipment')) {
      // For Equipment records, remove medical fields
      relevantFields = nonEmptyFields.filter(field => {
        if (['practice', 'userEmail', 'submittedAt', 'dateOfEvent', 'patientID', 'type'].includes(field)) {
          return true;
        }
        if (equipmentFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return true;
        }
        if (medicalFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return false;
        }
        return true;
      });
    } else if (type.includes('Complaint')) {
      // For Complaint records, remove medical fields
      relevantFields = nonEmptyFields.filter(field => {
        if (['practice', 'userEmail', 'submittedAt', 'dateOfEvent', 'patientID', 'type'].includes(field)) {
          return true;
        }
        if (complaintFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return true;
        }
        if (medicalFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return false;
        }
        return true;
      });
    } else if (type.includes('Scheduling')) {
      // For Scheduling records, remove medical fields
      relevantFields = nonEmptyFields.filter(field => {
        if (['practice', 'userEmail', 'submittedAt', 'dateOfEvent', 'patientID', 'type'].includes(field)) {
          return true;
        }
        if (schedulingFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return true;
        }
        if (medicalFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return false;
        }
        return true;
      });
    } else if (type.includes('Complication')) {
      // For Complication records, include ALL fields (don't filter out medical fields)
      // Just exclude IT/HIPAA fields that don't belong
      relevantFields = nonEmptyFields.filter(field => {
        // Always include common fields
        if (['practice', 'userEmail', 'submittedAt', 'dateOfEvent', 'patientID', 'patientPhoneNumber', 'type'].includes(field)) {
          return true;
        }
        // Include all medical/complication fields
        if (medicalFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return true;
        }
        // Exclude IT/HIPAA fields from complications
        if (itBreachFields.some(f => field.toLowerCase().includes(f.toLowerCase())) ||
            hipaaFields.some(f => field.toLowerCase().includes(f.toLowerCase()))) {
          return false;
        }
        // Include all other fields (including treatment options, emergencies, etc.)
        return true;
      });
    } else if (type.includes('Emergency')) {
      // For Emergency records, include common fields and emergency-specific fields
      relevantFields = nonEmptyFields.filter(field => {
        // Always include common fields (including patientID)
        if (['practice', 'userEmail', 'submittedAt', 'dateOfEvent', 'patientID', 'patientPhoneNumber', 'patientId', 'type', 'emergencies'].includes(field)) {
          return true;
        }
        // Include all other fields for emergencies
        return true;
      });
    } else if (type.includes('Success')) {
      // For Success records, include common fields and success-specific fields
      relevantFields = nonEmptyFields.filter(field => {
        // Always include common fields (including patientID/patientId)
        if (['practice', 'userEmail', 'submittedAt', 'dateOfEvent', 'patientID', 'patientId', 'patientPhoneNumber', 'patientPhone', 'type', 'otherInfo'].includes(field)) {
          return true;
        }
        // Include all other fields for success records
        return true;
      });
    }
    
    // Apply consistent ordering to all records
    const priorityOrder = [
      'type', 'userEmail', 'practice', 'patientID', 'patientId', 
      'submittedAt', 'dateOfEvent', 'emergencies', 
      'patientPhoneNumber', 'patientPhone', 'procedureId', 'procedureType'
    ];
    
    // Sort fields: priority fields first (in order), then remaining fields alphabetically
    relevantFields.sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a);
      const bIndex = priorityOrder.indexOf(b);
      
      // Both are priority fields - sort by priority order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // Only a is priority - a comes first
      if (aIndex !== -1) return -1;
      // Only b is priority - b comes first
      if (bIndex !== -1) return 1;
      // Neither is priority - sort alphabetically
      return a.localeCompare(b);
    });
    
    return relevantFields;
  }, []);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = [...data];

    // Apply search query (only searches within table header columns)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const headerFields = getPrimaryFields;
      filtered = filtered.filter(item => {
        return headerFields.some(field => {
          const value = item[field];
          const strValue = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
          return strValue.toLowerCase().includes(query);
        });
      });
    }

    // Apply filters (supports multiple values per field)
    Object.entries(filters).forEach(([key, values]) => {
      if (values && Array.isArray(values) && values.length > 0) {
        filtered = filtered.filter(item => {
          const itemValue = item[key];
          if (itemValue === null || itemValue === undefined) return false;
          
          // Handle array values
          if (Array.isArray(itemValue)) {
            return itemValue.some(iv => 
              values.some(filterVal => String(iv).toLowerCase() === String(filterVal).toLowerCase())
            );
          }
          
          // Handle single values
          return values.some(filterVal => 
            String(itemValue).toLowerCase() === String(filterVal).toLowerCase()
          );
        });
      }
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [data, searchQuery, filters, sortConfig, getPrimaryFields]);

  // Handle sort
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Toggle row expansion
  const toggleRowExpansion = useCallback((id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Handle filter modal open - initialize pending filters
  const handleOpenFilterModal = useCallback(() => {
    setPendingFilters({ ...filters });
    setSelectedFilterFields(new Set(Object.keys(filters)));
    setShowFilterModal(true);
  }, [filters]);

  // Toggle field selection in filter modal
  const toggleFieldSelection = useCallback((field) => {
    setSelectedFilterFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(field)) {
        newSet.delete(field);
        // Remove from pending filters if deselected
        setPendingFilters(prevFilters => {
          const newPending = { ...prevFilters };
          delete newPending[field];
          return newPending;
        });
      } else {
        newSet.add(field);
        // Initialize empty array for new field
        setPendingFilters(prevFilters => ({
          ...prevFilters,
          [field]: []
        }));
      }
      return newSet;
    });
  }, []);

  // Update filter values for a field
  const updatePendingFilterValues = useCallback((field, values) => {
    setPendingFilters(prev => ({
      ...prev,
      [field]: values
    }));
  }, []);

  // Apply filters from modal
  const applyFilters = useCallback(() => {
    // Only keep filters that have selected values
    const activeFilters = {};
    Object.entries(pendingFilters).forEach(([field, values]) => {
      if (values && Array.isArray(values) && values.length > 0) {
        activeFilters[field] = values;
      }
    });
    setFilters(activeFilters);
    setShowFilterModal(false);
  }, [pendingFilters]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFilters({});
    setPendingFilters({});
    setSelectedFilterFields(new Set());
    setSearchQuery('');
  }, []);

  // Get count of active filters
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(v => Array.isArray(v) && v.length > 0).length;
  }, [filters]);


  if (userRole !== 'System Administrator' && !userPractice) {
    return <div>Loading user data...</div>;
  }

  // Get color classes based on style
  const getColorClasses = (style) => {
    const colorMap = {
      green: { bg: 'bg-gray-50', border: 'border-gray-200', hoverBorder: 'hover:border-green-300', focusRing: 'focus:ring-green-500', iconColor: 'text-green-600' },
      red: { bg: 'bg-gray-50', border: 'border-gray-200', hoverBorder: 'hover:border-red-300', focusRing: 'focus:ring-red-500', iconColor: 'text-red-600' },
      orange: { bg: 'bg-gray-50', border: 'border-gray-200', hoverBorder: 'hover:border-orange-300', focusRing: 'focus:ring-orange-500', iconColor: 'text-orange-600' },
      safety: { bg: 'bg-gray-50', border: 'border-gray-200', hoverBorder: 'hover:border-blue-300', focusRing: 'focus:ring-blue-500', iconColor: 'text-blue-600' },
      ops: { bg: 'bg-gray-50', border: 'border-gray-200', hoverBorder: 'hover:border-emerald-300', focusRing: 'focus:ring-emerald-500', iconColor: 'text-emerald-600' }
    };
    return colorMap[style] || colorMap.ops;
  };

  const sections = [
    {
      title: 'Clinical Outcomes',
      icon: FaChartBar,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      buttons: pillButtons
    },
    {
      title: 'Safety & Security',
      icon: FaShieldAlt,
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      buttons: safetyButtons
    },
    {
      title: 'Operations & Quality',
      icon: FaClipboardCheck,
      iconColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      buttons: operationsButtons
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-8 sm:py-12">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-10 text-center">
          <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
            Procedure Completion Dashboard
          </h1>
          <p className="mt-3 text-lg text-gray-600">
            {userRole === 'System Administrator' ? 'All Practices' : userPractice}
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, sectionIdx) => (
            <div
              key={sectionIdx}
              className="divide-y divide-gray-200 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5 transition-shadow hover:shadow-xl"
            >
              {/* Section Header */}
              <div className={`px-6 py-5 ${section.bgColor} sm:px-6`}>
                <div className="flex items-center gap-3">
                  <div className={`flex size-12 items-center justify-center rounded-lg ${section.bgColor} ring-1 ring-inset ${section.iconColor === 'text-blue-600' ? 'border-blue-200' : section.iconColor === 'text-violet-600' ? 'border-violet-200' : 'border-indigo-200'}`}>
                    <section.icon className={`size-6 ${section.iconColor}`} aria-hidden="true" />
        </div>
                  <h2 className="text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">
                    {section.title}
                  </h2>
                </div>
              </div>

              {/* Cards Grid */}
              <div className="px-6 py-6 sm:px-6">
                {section.title === 'Clinical Outcomes' ? (
                  <dl className="mx-auto grid max-w-4xl grid-cols-2 gap-4">
                    {section.buttons.map((button, idx) => {
                      const colors = getColorClasses(button.style);
                      const IconComponent = button.icon;
                      return (
                        <button
                          key={`${sectionIdx}-${idx}`}
                          onClick={() => handleView(button.collection, button.subCollection, button.label)}
                          className={`group relative w-full overflow-hidden rounded-lg border-2 ${colors.border} ${colors.bg} px-6 py-8 text-center transition-all duration-200 ${colors.hoverBorder} hover:shadow-lg hover:-translate-y-1 outline-none`}
                        >
                          <div className="flex flex-col items-center gap-4">
                            <div className={`flex size-16 items-center justify-center rounded-lg bg-gray-50 transition-transform group-hover:scale-110`}>
                              <IconComponent className={`size-8 ${colors.iconColor}`} aria-hidden="true" />
                            </div>
                            <dt className="text-base font-semibold text-gray-900 line-clamp-2 min-h-[2.5rem] flex items-center">
                              {button.label}
                            </dt>
                            <dd className="text-4xl font-bold tracking-tight text-gray-900">
                              {buttonCounts[button.label] || 0}
                            </dd>
                          </div>
                        </button>
                      );
                    })}
                  </dl>
                ) : (
                  <dl className="mx-auto grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-3">
                    {section.buttons.map((button, idx) => {
                      const colors = getColorClasses(button.style);
                      const IconComponent = button.icon;
                      return (
                        <button
                          key={`${sectionIdx}-${idx}`}
                          onClick={() => handleView(button.collection, button.subCollection, button.label)}
                          className={`group relative overflow-hidden rounded-lg border-2 ${colors.border} ${colors.bg} px-4 py-6 text-center transition-all duration-200 ${colors.hoverBorder} hover:shadow-lg hover:-translate-y-1 outline-none`}
                        >
                          <div className="flex flex-col items-center gap-3">
                            <div className={`flex size-12 items-center justify-center rounded-lg bg-gray-50 transition-transform group-hover:scale-110`}>
                              <IconComponent className={`size-6 ${colors.iconColor}`} aria-hidden="true" />
                            </div>
                            <dt className="text-sm font-semibold text-gray-900 line-clamp-2 min-h-[2.5rem] flex items-center">
                              {button.label}
                            </dt>
                            <dd className="text-3xl font-bold tracking-tight text-gray-900">
                              {buttonCounts[button.label] || 0}
                            </dd>
                          </div>
                        </button>
                      );
                    })}
                  </dl>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Data Viewer Section */}
        {currentView && (
          <div ref={dataViewerRef} className="mt-10 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{currentView} Details</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {filteredAndSortedData.length} of {data.length} records
                  </p>
      </div>
        </div>
      </div>

            {/* Filters and Search */}
            <div className="border-b border-gray-200 bg-white px-6 py-4 sm:px-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                {/* Search Bar */}
                <div className="flex items-center gap-3 flex-1">
                  <FaSearch className="size-5 text-gray-400 sm:size-4" aria-hidden="true" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search table columns..."
                    className="block flex-1 rounded-md border-0 bg-white py-1.5 px-3 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                  />
        </div>

                {/* Filter Button and Clear Filters */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleOpenFilterModal}
                    className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  >
                    <FaFilter className="size-4 text-gray-400" />
                    Filter
                    {activeFilterCount > 0 && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-medium text-white">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={clearAllFilters}
                      className="inline-flex items-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500"
                    >
                      <FaTimes className="size-4 text-gray-400" />
                      Clear
                    </button>
                  )}
            </div>
              </div>
            </div>

            {/* Data Table */}
            <div className="px-6 py-6 sm:px-6">
              {loading ? (
                <div className="py-12 text-center">
                  <div className="inline-block size-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
                  <p className="mt-4 text-sm text-gray-600">Loading...</p>
                </div>
              ) : filteredAndSortedData.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-gray-600">No data matches your filters</p>
                </div>
              ) : (
                <div className="flow-root">
                  <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead>
                          <tr>
                            <th scope="col" className="w-12 py-3.5 pl-4 pr-3 sm:pl-0"></th>
                            {getPrimaryFields.map(field => (
                              <th
                                key={field}
                                scope="col"
                                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-50"
                                onClick={() => handleSort(field)}
                              >
                                <div className="flex items-center gap-2">
                                  <span>{formatFieldLabel(field)}</span>
                                  {sortConfig.key === field && (
                                    <span className="text-indigo-600">
                                      {sortConfig.direction === 'asc' ? <FaChevronUp className="size-4" /> : <FaChevronDown className="size-4" />}
                                    </span>
                                  )}
                                </div>
                              </th>
                            ))}
                            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                              <span className="sr-only">Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {filteredAndSortedData.map((item, idx) => (
                            <React.Fragment key={item.id}>
                              <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                                  <button
                                    onClick={() => toggleRowExpansion(item.id)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                  >
                                    {expandedRows.has(item.id) ? (
                                      <FaChevronUp className="size-4" />
                                    ) : (
                                      <FaChevronDown className="size-4" />
                                    )}
                                  </button>
                                </td>
                                {getPrimaryFields.map(field => {
                                  const fieldValue = item[field];
                                  // Use formatValueForDisplay for consistent formatting (dates, phones, arrays, etc.)
                                  const displayValue = formatValueForDisplay(fieldValue, field);
                                  const isReactElement = typeof displayValue === 'object' && displayValue !== null && displayValue.$$typeof;
                                  
                                  return (
                                    <td key={field} className={`px-3 py-4 text-sm text-gray-500 ${isReactElement ? '' : 'whitespace-nowrap'}`}>
                                      {displayValue}
                                    </td>
                                  );
                                })}
                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                                  <div className="flex items-center justify-end gap-2">
                                    {userRole === 'System Administrator' && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(item); }}
                                        className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                                        title="Remove entry"
                                      >
                                        <FaTrash className="size-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedRows.has(item.id) && (
                                <tr>
                                  <td colSpan={getPrimaryFields.length + 2} className="px-4 py-4 bg-gray-50">
                                    <div className="rounded-lg bg-white p-4 shadow-sm">
                                      <h4 className="mb-4 text-sm font-semibold text-gray-900">Full Details</h4>
                                      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                        {getRelevantFields(item).map((key) => {
                                          const value = item[key];
                                          return (
                                            <div key={key} className="flex flex-col">
                                              <dt className="text-xs font-medium text-gray-500">{formatFieldLabel(key)}</dt>
                                              <dd className="mt-1 text-sm text-gray-900 break-words">
                                                {formatValueForDisplay(value, key)}
                                              </dd>
      </div>
                                          );
                                        })}
                                      </dl>
      </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
        </div>
      </div>
              )}
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <Dialog open={!!deleteConfirmItem} onClose={handleCancelDelete} className="relative z-10">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <DialogPanel
                transition
                className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-md sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
              >
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                    <FaExclamationTriangle className="size-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                      Remove entry
                    </DialogTitle>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to remove this entry? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-3">
                  <button
                    type="button"
                    disabled={isDeleting}
                    onClick={handleConfirmDelete}
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 sm:w-auto"
                  >
                    {isDeleting ? 'Removing...' : 'Remove'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    disabled={isDeleting}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>

        {/* Filter Modal */}
        <Dialog open={showFilterModal} onClose={setShowFilterModal} className="relative z-10">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          />

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <DialogPanel
                transition
                className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
              >
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    onClick={() => setShowFilterModal(false)}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600"
                  >
                    <span className="sr-only">Close</span>
                    <FaTimes aria-hidden="true" className="size-6" />
                  </button>
        </div>

                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:size-10">
                    <FaFilter aria-hidden="true" className="size-6 text-indigo-600" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                      Filter Records
                    </DialogTitle>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Filter by the columns shown in the table header.
                      </p>
                    </div>
        </div>
      </div>

                <div className="mt-5 max-h-96 overflow-y-auto">
                  {/* Field Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select fields to filter:
                    </label>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {getPrimaryFields.map(field => (
                        <div key={field} className="relative flex items-start">
                          <div className="flex h-6 items-center">
                            <input
                              id={`field-${field}`}
                              name={`field-${field}`}
                              type="checkbox"
                              checked={selectedFilterFields.has(field)}
                              onChange={() => toggleFieldSelection(field)}
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            />
                          </div>
                          <div className="ml-3 text-sm leading-6">
                            <label htmlFor={`field-${field}`} className="font-medium text-gray-900 cursor-pointer">
                              {formatFieldLabel(field)}
                            </label>
        </div>
            </div>
          ))}
        </div>
      </div>

                  {/* Value Selection for Selected Fields */}
                  {Array.from(selectedFilterFields).length > 0 && (
                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Select values for each field:
                      </label>
                      {Array.from(selectedFilterFields).map(field => (
                        <div key={`filter-values-${field}`}>
                          <label htmlFor={`values-${field}`} className="block text-sm font-medium text-gray-700 mb-2">
                            {formatFieldLabel(field)}
                          </label>
                          <select
                            id={`values-${field}`}
                            multiple
                            size={Math.min(getUniqueValuesForField(field).length, 8)}
                            value={pendingFilters[field] || []}
                            onChange={(e) => {
                              const options = Array.from(e.target.selectedOptions);
                              const selectedValues = options.map(option => option.value);
                              updatePendingFilterValues(field, selectedValues);
                            }}
                            className="block w-full rounded-md border-0 bg-white py-2 pl-3 pr-10 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 sm:text-sm/6"
                          >
                            {getUniqueValuesForField(field).map(value => (
                              <option key={value} value={value}>
                                {value.length > 50 ? `${value.substring(0, 50)}...` : value}
                              </option>
                            ))}
                          </select>
                          <p className="mt-1 text-xs text-gray-500">
                            Hold Ctrl/Cmd to select multiple values
                          </p>
                        </div>
                      ))}
        </div>
      )}
                </div>

                <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:ml-3 sm:w-auto"
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowFilterModal(false)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

export default DataView;
